import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  Injector,
  computed,
  inject,
  runInInjectionContext,
} from '@angular/core'

import { Store } from '@ngrx/store'
import { ImageModule } from 'primeng/image'
import { ProgressBarModule } from 'primeng/progressbar'

import { take } from 'rxjs'

import { FlxIconModule } from '@flowx/angular-ui-toolkit'
import {
  AgentAction,
  AgentName,
} from '@flowx/designer/modules/ai/conversation/ai-conversation.commons'
import { ContextDataKeys } from '@flowx/designer/modules/ai/conversation/ai-conversation.model'
import { ConversationSelectors } from '@flowx/designer/modules/ai/conversation/conversation.selectors'
import { AiChatHistoryComponent } from '@flowx/designer/modules/ai/modules/chat/components/chat-history/chat-history.component'
import { AiTypeAreaComponent } from '@flowx/designer/modules/ai/modules/chat/components/type-area/type-area.component'
import { convertBlobToBase64 } from '@flowx/designer/shared/utils/file.utils'

import { confirmDiscardContext } from '../../common/guards/pending-context.guard'
import { ConversationActions } from '../../conversation/conversation.actions'
import { AiActions, BreadcrumbActions } from '../../store/ai.actions'
import { AiSelectors } from '../../store/ai.selectors'
import { ContextFile } from '../../types/ai.types'

const typeAreaHintMap: Partial<
  Record<AgentName, Partial<Record<AgentAction | 'Default', string>> | string>
> = {
  [AgentName.AiAssistant]: 'Ask me to explain or do something...',
  [AgentName.AiAnalyst]: 'Describe the Process you want to create in detail',
  [AgentName.AiDesigner]: 'Describe the UI you want to create in detail',
  [AgentName.AiDeveloper]: {
    [AgentAction.BusinessRule]: 'Describe the business rule you want created',
    [AgentAction.Expression]: 'Describe the expression you want to create',
    [AgentAction.ComputedValue]: 'Describe the computed value you want to create',
    [AgentAction.CustomComponent]: 'Describe the custom component you want to create',
    Default: 'Describe the business rule you want created',
  },
}
const defaultTypeAreaPlaceholder = 'Ask me to explain or do something...'

@Component({
  selector: 'flx-command-center-chat',
  imports: [
    CommonModule,
    AiTypeAreaComponent,
    FlxIconModule,
    ProgressBarModule,
    AiChatHistoryComponent,
    ImageModule,
  ],
  templateUrl: './command-center-chat.component.html',
  styleUrl: './command-center-chat.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'flex flex-col flex-1 min-h-0',
  },
})
export class CommandCenterChatComponent {
  #store = inject(Store)
  #injector = inject(Injector)

  isCloseContextDocumentDisabled = this.#store.selectSignal(AiSelectors.pendingAiResponse)
  latestMessageFromAgent = this.#store.selectSignal(ConversationSelectors.latestMessageFromAgent)

  typeAreaPlaceholder = computed(() => {
    const executionPlanAgent = this.latestMessageFromAgent()?.executionPlan?.actions[0]?.agent
    const contextAgent = JSON.parse(
      this.latestMessageFromAgent()?.contextData.items.find(
        (item) => item.key === ContextDataKeys.UiAgent
      )?.valueGenericString || '{}'
    )
    const agent = contextAgent?.agent || executionPlanAgent?.agent
    const action = contextAgent?.action || executionPlanAgent?.action
    let hint = typeAreaHintMap[agent]
    if (typeof hint === 'object' && action) {
      hint = hint[action] || hint['Default']
    }
    return hint || defaultTypeAreaPlaceholder
  })

  messages = this.#store.selectSignal(ConversationSelectors.chatMessages)
  images = computed(() =>
    this.#store
      .selectSignal(ConversationSelectors.uploadedImageFiles)()
      .map(({ id, file }) => ({
        id,
        name: file.name,
        src$: convertBlobToBase64(file),
      }))
  )
  bpmnFiles = computed(() =>
    this.#store
      .selectSignal(ConversationSelectors.uploadedBpmnFiles)()
      .map(({ id, file }) => ({
        id,
        name: file.name,
      }))
  )
  showUploadedImages = computed(
    () => !this.#store.selectSignal(AiSelectors.hasSelectedDocument)() && this.images()?.length
  )
  showUploadedBpmnFiles = computed(() => this.bpmnFiles()?.length)
  uploadingDocuments = this.#store.selectSignal(AiSelectors.uploadingDocuments)
  isUploadingDocuments = this.#store.selectSignal(AiSelectors.isUploadingDocuments)
  selectedDocument = this.#store.selectSignal(AiSelectors.selectedDocument)
  showContextDocuments = computed(() => this.isUploadingDocuments() || this.selectedDocument())
  contextDocumentsStatus = computed(() => (this.isUploadingDocuments() ? 'Uploading' : 'Context'))
  contextDocumentsName = computed(
    () => this.uploadingDocuments()?.[0]?.name || this.selectedDocument()?.name
  )

  mainOutcome = this.#store.selectSignal(ConversationSelectors.mainOutcome)
  agentProcessingMessage = this.#store.selectSignal(ConversationSelectors.agentProcessingMessage)
  activeAgentThreadExists = this.#store.selectSignal(ConversationSelectors.activeAgentThreadExists)
  defaultThreadExists = this.#store.selectSignal(ConversationSelectors.defaultThreadExists)
  currentExecutionPlanExists = this.#store.selectSignal(
    ConversationSelectors.currentExecutionPlanExists
  )
  conversationExists = this.#store.selectSignal(ConversationSelectors.conversationExists)

  showChatTypeArea = computed(() => {
    const activeAgentThreadExists = this.activeAgentThreadExists()
    const defaultThreadExists = this.defaultThreadExists()
    const mainOutcome = this.mainOutcome()
    const conversationExists = this.conversationExists()

    const activeAgentThreadWithNoMainOutcome = activeAgentThreadExists && !mainOutcome

    const result = activeAgentThreadWithNoMainOutcome || !conversationExists || defaultThreadExists

    return result
  })

  constructor() {}

  confirmRemoveDocumentFromActiveContext(): void {
    runInInjectionContext(this.#injector, () => {
      confirmDiscardContext({
        message: `You are about to lose the generated data in the current session. <br/> Are you sure you want to discard the document?`,
        onSuccess: () => {},
      })
        .pipe(take(1))
        .subscribe()
    })
  }
  onBreadcrumbsBack(): void {
    this.#store.dispatch(BreadcrumbActions.backClick())
  }
  onContextDocumentCloseClick(): void {
    if (this.#store.selectSignal(AiSelectors.hasDraft)()) {
      this.confirmRemoveDocumentFromActiveContext()
    } else {
      this.#store.dispatch(AiActions.contextDocumentCloseClick())
    }
  }

  deleteFileFromContext(imageId: ContextFile['id']): void {
    this.#store.dispatch(ConversationActions.fileRemoveClick({ id: imageId }))
  }
}
